import { useState, useEffect, useMemo } from "react"
import Cookies from "js-cookie"
import costPerMile from "../constants/cost_per_mile.json"
import { replaceValue } from "../util/string"
import geo from "../services/geoLocation"
import { GEO_FETCH_DELAY_MS } from "../constants/geo"

export const useGeoDataEffect = (geoPostDescription: string) => {
  const [geoDescription, setGeoDescription] = useState("")
  const [geoSessionValue, setGeoSessionValue] = useState<string | null>(null)
  const [isUSVisitor, setIsUSVisitor] = useState(true)

  useEffect(() => {
    const cookieValue = Cookies.get("geoLocation") || null
    if (cookieValue && cookieValue !== geoSessionValue) {
      setGeoSessionValue(cookieValue)
    }
  }, [])

  useEffect(() => {
    // Process cached data immediately if available
    const processCachedData = () => {
      if (!geoSessionValue) return false

      try {
        const geoData = JSON.parse(geoSessionValue)
        if (geoData && geoData.country_code2 === "US") {
          const geoCity = geoData.city || ""
          const matchingLocation = costPerMile.find(
            (location) => location.city.toLowerCase() === geoCity.toLowerCase(),
          )
          const cost_per_mile = matchingLocation
            ? matchingLocation.cost_per_mile
            : costPerMile.find(
                (location) => location.city.toLowerCase() === "overall",
              )?.cost_per_mile ?? ""

          const updatedDescription = replaceValue(geoPostDescription, {
            location: geoCity,
            cost_per_mile,
          })
          setGeoDescription(updatedDescription)
          return true
        } else {
          setIsUSVisitor(false)
          return true
        }
      } catch (error) {
        console.warn("Error parsing geoSessionValue:", error)
        Cookies.remove("geoLocation")
        setGeoSessionValue(null)
        return false
      }
    }

    // If we have cached data, use it immediately
    if (processCachedData()) {
      return
    }

    // Defer fresh geo fetch to avoid blocking LCP/TBT
    const timeoutId = setTimeout(async () => {
      try {
        const rawGeoData = await geo.getGeoLocation()
        let decodedGeoData = rawGeoData
        let geoData = null

        try {
          if (rawGeoData?.includes("%22")) {
            decodedGeoData = decodeURIComponent(rawGeoData)
          }
          geoData = JSON.parse(decodedGeoData!)
        } catch (parseError) {
          console.warn("Error parsing geoData:", parseError)
          return
        }

        if (geoData?.country_code2 === "US") {
          Cookies.set("geoLocation", decodedGeoData!, { expires: 7 })
          setGeoSessionValue(decodedGeoData)

          const geoCity = geoData.city || ""
          const matchingLocation = costPerMile.find(
            (location) => location.city.toLowerCase() === geoCity.toLowerCase(),
          )
          const cost_per_mile = matchingLocation
            ? matchingLocation.cost_per_mile
            : costPerMile.find(
                (location) => location.city.toLowerCase() === "overall",
              )?.cost_per_mile ?? ""

          const updatedDescription = replaceValue(geoPostDescription, {
            location: geoCity,
            cost_per_mile,
          })
          setGeoDescription(updatedDescription)
        } else {
          setIsUSVisitor(false)
        }
      } catch (error) {
        console.warn("Error fetching geolocation:", error)
      }
    }, GEO_FETCH_DELAY_MS)

    return () => clearTimeout(timeoutId)
  }, [geoPostDescription, geoSessionValue])

  const values = useMemo(() => {
    return { geoDescription, geoSessionValue, isUSVisitor }
  }, [geoDescription, geoSessionValue, isUSVisitor])

  return values
}
